/**
 * SettingsLayout Component
 * A modern settings page layout with sidebar navigation and responsive design
 * Follows the pattern used by Linear, Stripe, and other modern SaaS products
 */

import { cn } from '@/utils/cn'
import { ChevronLeft, type LucideIcon } from 'lucide-react'
import { useState, createContext, useContext, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'

interface SettingsSection {
  id: string
  label: string
  icon: LucideIcon
  description?: string
}

interface SettingsLayoutContextType {
  activeSection: string
  setActiveSection: (id: string) => void
  isMobileSidebarOpen: boolean
  setMobileSidebarOpen: (open: boolean) => void
}

const SettingsLayoutContext = createContext<SettingsLayoutContextType | null>(null)

function useSettingsLayout() {
  const context = useContext(SettingsLayoutContext)
  if (!context) {
    throw new Error('useSettingsLayout must be used within SettingsLayout')
  }
  return context
}

interface SettingsLayoutProps {
  sections: SettingsSection[]
  defaultSection?: string
  children: ReactNode
}

export function SettingsLayout({ sections, defaultSection, children }: SettingsLayoutProps) {
  const [activeSection, setActiveSection] = useState(defaultSection || sections[0]?.id || '')
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  return (
    <SettingsLayoutContext.Provider
      value={{ activeSection, setActiveSection, isMobileSidebarOpen, setMobileSidebarOpen }}
    >
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 min-h-[calc(100vh-12rem)]">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between pb-4 border-b border-border/50">
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <span className="text-foreground">
              {sections.find(s => s.id === activeSection)?.label || 'Settings'}
            </span>
            <ChevronLeft className="h-4 w-4 rotate-[-90deg]" />
          </button>
        </div>

        {/* Sidebar */}
        <aside className={cn(
          // Base styles
          "shrink-0",
          // Desktop: sticky sidebar
          "lg:sticky lg:top-6 lg:self-start lg:w-56",
          // Mobile: fullscreen overlay
          "fixed inset-0 z-50 bg-background/95 backdrop-blur-sm lg:relative lg:bg-transparent lg:backdrop-blur-none",
          "transition-all duration-200",
          isMobileSidebarOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none lg:opacity-100 lg:pointer-events-auto"
        )}>
          {/* Mobile close button */}
          <div className="lg:hidden flex items-center justify-between p-4 border-b border-border/50">
            <span className="text-sm font-semibold text-foreground">Settings</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileSidebarOpen(false)}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </div>

          <nav className="p-4 lg:p-0 space-y-1" role="navigation" aria-label="Settings navigation">
            {sections.map((section) => {
              const isActive = activeSection === section.id
              const Icon = section.icon

              return (
                <button
                  key={section.id}
                  onClick={() => {
                    setActiveSection(section.id)
                    setMobileSidebarOpen(false)
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150",
                    "text-sm font-medium",
                    isActive
                      ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-transparent"
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon className={cn(
                    "h-4 w-4 flex-shrink-0 transition-colors",
                    isActive ? "text-blue-400" : "text-muted-foreground"
                  )} />
                  <span>{section.label}</span>
                </button>
              )
            })}
          </nav>
        </aside>

        {/* Main content area */}
        <main className="flex-1 min-w-0">
          {children}
        </main>
      </div>
    </SettingsLayoutContext.Provider>
  )
}

interface SettingsSectionContentProps {
  id: string
  title: string
  description?: string
  children: ReactNode
}

export function SettingsSectionContent({ id, title, description, children }: SettingsSectionContentProps) {
  const { activeSection } = useSettingsLayout()

  if (activeSection !== id) {
    return null
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-2 duration-200">
      <header className="pb-4 border-b border-border/50">
        <h2 className="text-xl font-semibold text-foreground">{title}</h2>
        {description && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
      </header>
      <div className="space-y-6">
        {children}
      </div>
    </div>
  )
}

export { useSettingsLayout }
